import { FC } from "react";
import {
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import { getFormattedValue, numberWithCommas } from "@/utils";
import { tokens } from "@/tokens";

/* ------------------------------------------------------------------ */
/*  Shared cell style constants                                       */
/* ------------------------------------------------------------------ */

const cellBase = {
  fontSize: tokens.fontSize.xs,
  fontWeight: tokens.fontWeight.semibold,
  lineHeight: "16px",
  whiteSpace: "nowrap" as const,
};

const firstRadius = { borderRadius: `${tokens.radius.md} 0 0 ${tokens.radius.md}` };
const lastRadius = { borderRadius: `0 ${tokens.radius.md} ${tokens.radius.md} 0` };

/* ------------------------------------------------------------------ */
/*  Header                                                            */
/* ------------------------------------------------------------------ */

interface ColumnDef {
  name: string;
  key: string | number | symbol;
  align?: "left" | "right" | "center";
  width?: string;
  minWidth?: string;
}

interface AnalyticsTableHeaderProps {
  columns: ColumnDef[];
}

export const AnalyticsTableHeader: FC<AnalyticsTableHeaderProps> = ({ columns }) => (
  <TableHead>
    <TableRow>
      {columns.map((col, idx) => (
        <TableCell
          key={`head-${String(col.key)}`}
          align={col.align}
          sx={{
            ...cellBase,
            width: col.width,
            minWidth: col.minWidth || "auto",
            padding: { xs: "3px 4px !important", md: "5px 6px !important" },
            backgroundColor: `${tokens.bg.muted} !important`,
            color: `${tokens.text.primary} !important`,
            textTransform: "none",
            letterSpacing: "normal",
            ...(idx === 0 && firstRadius),
            ...(idx === columns.length - 1 && lastRadius),
          }}
        >
          {col.name}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

/* ------------------------------------------------------------------ */
/*  Footer                                                            */
/* ------------------------------------------------------------------ */

interface AnalyticsTableFooterProps {
  sections: VendorSection[];
  data: Record<string, number>;
  columns: ColumnDef[];
  /** Override the label shown for `type: "total"`, e.g. "Total (5)" */
  totalLabel?: string;
}

export const AnalyticsTableFooter: FC<AnalyticsTableFooterProps> = ({
  sections,
  data,
  columns,
  totalLabel,
}) => (
  <TableFooter>
    <TableRow sx={{ position: "sticky", bottom: 0 }}>
      {sections.map((section, index) => {
        const col = columns[index];
        let value: string | number = "";

        if (section.type === "total") {
          value = totalLabel ?? section.name;
        } else if (section.type === "empty") {
          value = "";
        } else if (section.type === "field") {
          const fieldName = section.fieldName;
          const raw = data[fieldName] ?? 0;
          value =
            fieldName === "effectiveDutyRate" || fieldName === "percentCustomValue" || fieldName === "percentOfTotal"
              ? `${raw.toFixed(2)}%`
              : getFormattedValue(numberWithCommas(raw), section.symbol ?? "", col?.align ?? "left");
        }

        return (
          <TableCell
            key={`footer-${index}`}
            align={col?.align ?? "left"}
            sx={{
              ...cellBase,
              width: col?.width,
              minWidth: col?.minWidth || "auto",
              padding: { xs: "6px 4px !important", md: "8px 6px !important" },
              backgroundColor: `${tokens.bg.muted} !important`,
              color: tokens.text.primary,
              ...(index === 0 && firstRadius),
              ...(index === sections.length - 1 && lastRadius),
            }}
          >
            {value}
          </TableCell>
        );
      })}
    </TableRow>
  </TableFooter>
);
